<template>
  <div class="modern-header">
    <!-- Левый блок: Логотип, название и toolbar -->
    <div class="header-left">
      <div class="header-brand">
        <router-link :to="{ name: 'home' }" class="header-logo-link">
          <div class="logo-container">
            <div class="logo-wrapper" :class="{ 'logo-wrapper--bare': isSpdClient }">
              <img :src="LogoPath" :alt="config.LOGO_TITLE" class="logo-image" />
            </div>
            <div class="logo-text">
              <span class="logo-title">{{ config.LOGO_TITLE }}</span>
              <span class="logo-subtitle">{{ config.LOGO_SUBTITLE }}</span>
              <AppVersion tone="light" class="logo-version" />
            </div>
          </div>
        </router-link>
        <Button
          v-if="withSideNavigation && isAuth"
          type="button"
          :icon="menuToggleIcon"
          class="action-btn p-button-text header-menu-btn"
          :aria-label="drawerOpen ? 'Закрыть меню' : 'Открыть меню'"
          :aria-expanded="drawerOpen"
          @click="emit('toggle-side-nav')"
        />
      </div>
      <ToolbarMenu v-if="isAuth" />
    </div>

    <!-- Центральный блок: Навигация и поиск -->
    <div class="header-center">
      <InputText v-if="showSearch" :placeholder="t('platform.searchPlaceholder')" class="search-field" />
    </div>

    <!-- Правый блок: Инструменты и профиль -->
    <div class="header-right" v-if="isAuth">
      <!-- Таймер остановки платформы -->
      <ShutdownTimer />

      <div class="tools-container">
        <!-- WebSocket Status: только не-prod (dev/test) -->
        <WebSocketStatus v-if="showWebSocketStatus" />

        <!-- Устройства -->
        <router-link v-if="useDeviceBridge" :to="{ name: 'devices' }" class="devices-link">
          <Button icon="pi pi-microchip" class="action-btn p-button-text">
            <Badge v-if="connectedDevicesCount > 0" :value="connectedDevicesCount" severity="success" />
          </Button>
          <!-- Service Status Indicator -->
          <div class="service-status-indicator" :class="{ 'running': isServiceRunning, 'stopped': !isServiceRunning }" :title="isServiceRunning ? t('platform.serviceRunning') : t('platform.serviceStopped')">
            <span class="status-dot"></span>
          </div>
        </router-link>
      </div>

      <span
        v-if="platformIdLabel"
        class="platform-id"
        :title="t('platform.platformLabel', { id: platformIdLabel })"
      >{{ platformIdLabel }}</span>
      <ProfileCard />
      <UserPresenceIndicator />
    </div>

    <!-- Индикатор загрузки -->
    <div v-if="isLoading" class="loading-indicator" />
  </div>
</template>

<script setup lang="ts">
import { configService } from '@/common/utils/clientConfig'
import LogoPath from '@/common/constants/logo';
import AppVersion from '@/components/app/AppVersion.vue'
import ProfileCard from '@/components/app/ProfileCard.vue'
import UserPresenceIndicator from '@/components/app/UserPresenceIndicator.vue'
import ShutdownTimer from '@/components/app/ShutdownTimer.vue'
import WebSocketStatus from '@/components/app/WebSocketStatus.vue'
import ToolbarMenu from '@/components/app/toolbar/ToolbarMenu.vue'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useServiceStore } from '@/stores/service'
import { deviceService } from '@/services/device/device.service'
import { useI18n } from '@/common/composables/useI18n'
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

interface Props {
  withSideNavigation?: boolean
  drawerOpen?: boolean
  menuToggleAlwaysBars?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  withSideNavigation: true,
  drawerOpen: false,
  menuToggleAlwaysBars: false,
})

const emit = defineEmits<{
  'toggle-side-nav': []
}>()

// Stores
const userStore = useUserStore()
const appStore = useAppStore()
const serviceStore = useServiceStore()
const { t } = useI18n()

// Local state
const showSearch = ref(false)
const connectedDevicesCount = ref(0)

// Computed
const isAuth = computed(() => userStore.isAuthenticated)
const isLoading = computed(() => appStore.isLoading)
const config = computed(() => configService.getAll())
const isSpdClient = computed(() => config.value.CLIENT === 'spd')
const menuToggleIcon = computed(() => {
  if (props.menuToggleAlwaysBars) return 'pi pi-bars'
  return props.drawerOpen ? 'pi pi-times' : 'pi pi-bars'
})
const isServiceRunning = computed(() => serviceStore.isServiceRunning)
const showWebSocketStatus = computed(() => configService.isNonProductionMode())
const platformIdLabel = computed(() => {
  if (!configService.isNonProductionMode()) return ''
  const raw = appStore.configData?.platformId
  if (raw == null) return ''
  const trimmed = String(raw).trim()
  return trimmed
})

// Update connected devices count
const updateDevicesCount = () => {
  if (serviceStore.isServiceRunning) {
    const status = serviceStore.status
    connectedDevicesCount.value = status.connectedDevices || 0
  } else {
    connectedDevicesCount.value = 0
  }
}

const handleDeviceList = (devices: any[]) => {
  const connected = devices.filter(d => d.connected).length;
  connectedDevicesCount.value = connected;
}

const handleDeviceConnected = () => {
  if (deviceService.isConnected()) {
    deviceService.discoverDevices();
  }
}

const handleDeviceDisconnected = () => {
  if (deviceService.isConnected()) {
    deviceService.discoverDevices();
  }
}

const useDeviceBridge = computed(() => {
  const envValue = import.meta.env.VITE_USE_DEVICE_BRIDGE;
  // Default to true if not set (backward compatibility)
  return envValue === undefined || envValue === '' || envValue.toLowerCase() === 'true';
});

onMounted(async () => {
  if (!useDeviceBridge.value) return; // Skip if device bridge is disabled

  updateDevicesCount()

  // Connect to device service if service is running
  if (serviceStore.isServiceRunning && !deviceService.isConnected()) {
    try {
      await deviceService.connect();
        } catch (err) {
          // Connection failed - will retry
        }
  }

  deviceService.on('deviceList', handleDeviceList);
  deviceService.on('deviceConnected', handleDeviceConnected);
  deviceService.on('deviceDisconnected', handleDeviceDisconnected);

  // Watch for service status changes
  watch(() => serviceStore.isServiceRunning, async (running) => {
    if (!useDeviceBridge.value) return; // Skip if device bridge is disabled

    if (running) {
      updateDevicesCount();
      // Connect to device service
      if (!deviceService.isConnected()) {
        try {
          await deviceService.connect();
          // Request device list
          deviceService.discoverDevices();
        } catch (err) {
          // Connection failed - will retry
        }
      } else {
        // Request device list if already connected
        deviceService.discoverDevices();
      }
    } else {
      connectedDevicesCount.value = 0;
    }
  });
})

onUnmounted(() => {
  deviceService.off('deviceList', handleDeviceList);
  deviceService.off('deviceConnected', handleDeviceConnected);
  deviceService.off('deviceDisconnected', handleDeviceDisconnected);
})
</script>

<style scoped lang="scss">
.modern-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: var(--app-theme-primary, #1e3a6e);
  border-bottom: 1px solid color-mix(in srgb, var(--app-theme-primary, #1e3a6e) 75%, black);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 20px;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-logo-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;

  &:hover {
    .logo-wrapper:not(.logo-wrapper--bare) {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-1px);
    }

    .logo-wrapper--bare .logo-image {
      transform: scale(1.05);
    }
  }
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 6px;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

/* SPD SVG already has its own plate — no glass frame */
.logo-wrapper--bare {
  background: transparent;
  border: none;
  padding: 0;
  backdrop-filter: none;
  border-radius: 0;

  .logo-image {
    width: 100%;
    height: 100%;
  }
}

.logo-image {
  width: 40px;
  height: 40px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.logo-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.logo-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.3px;
  margin-top: 2px;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

.logo-version {
  margin-top: 2px;
}

.header-center {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  justify-content: center;
  max-width: 400px;
  margin: 0 40px;
}

.search-field {
  width: 300px;

  :deep(.p-inputtext) {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:focus {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    }
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.platform-id {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  white-space: nowrap;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

.tools-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.service-status-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  cursor: pointer;
  z-index: 10;
  pointer-events: none;

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
    border: 1.5px solid rgba(255, 255, 255, 0.95);
  }

  &.running {
    .status-dot {
      background-color: #4caf50;
      box-shadow: 0 0 0 1px rgba(76, 175, 80, 0.3);
      animation: pulse-green 2s infinite;
    }
  }

  &.stopped {
    .status-dot {
      background-color: #f44336;
      box-shadow: 0 0 0 1px rgba(244, 67, 54, 0.3);
    }
  }
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.action-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.12) !important;
  width: 42px;
  height: 42px;
  border: none;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.devices-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  position: relative;
}

.loading-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--app-theme-primary, #42a5f5) 70%, white),
    var(--app-theme-primary, #42a5f5),
    color-mix(in srgb, var(--app-theme-primary, #2196f3) 85%, black)
  );
  animation: loading 1.5s infinite;
  border-radius: 2px;
}

@keyframes loading {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.header-menu-btn {
  display: none;
  flex-shrink: 0;
}

// Адаптивность
@media (max-width: 960px) {
  .modern-header {
    padding: 0 20px;
  }

  .header-menu-btn {
    display: inline-flex;
  }

  .logo-text {
    display: none;
  }

  .logo-wrapper {
    width: 50px;
    height: 50px;
    border-radius: 10px;
  }

  .logo-wrapper--bare {
    border-radius: 0;
  }

  .logo-image {
    width: 36px;
    height: 36px;
  }

  .logo-wrapper--bare .logo-image {
    width: 100%;
    height: 100%;
  }

  .header-center {
    margin: 0 20px;
    gap: 15px;
  }
}

@media (max-width: 768px) {
  .header-center {
    display: none;
  }

  .logo-container {
    gap: 12px;
  }

  .header-left {
    gap: 10px;
  }

  .platform-id {
    display: none;
  }
}

@media (max-width: 480px) {
  .modern-header {
    padding: 0 16px;
  }

  .logo-wrapper {
    width: 44px;
    height: 44px;
    padding: 5px;
  }

  .logo-wrapper--bare {
    padding: 0;
  }

  .logo-image {
    width: 32px;
    height: 32px;
  }

  .logo-wrapper--bare .logo-image {
    width: 100%;
    height: 100%;
  }

  .tools-container {
    gap: 6px;
  }

  .action-btn {
    width: 36px;
    height: 36px;
  }
}
</style>
